import Box from '@locus-labs/mol-mobile-box'
import Icon from '@locus-labs/mol-mobile-icon'
import Text from '@locus-labs/mol-mobile-text'
import styledSystemPropTypes from '@styled-system/prop-types'
import PropTypes from 'prop-types'
import React, { forwardRef, useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { ifProp } from 'styled-tools'

import { getThemeColor } from '../../utils/styled-component-tools'

const EASE_OUT_DURATION = 200

const showKeyframe = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`

const StyledToast = styled(Box)`
  place-self: center;
  animation: ${showKeyframe} 100ms ease-in;
  border-radius: ${({ theme })=> theme.standardRadius};
  &.hide {
    transition: opacity ${EASE_OUT_DURATION}ms ease-out;
    opacity: 0;
  }

  &.withVerticalTransition {
    transition: transform 200ms linear, opacity ${EASE_OUT_DURATION}ms ease-out;
  }
  &.withHorizontalTransition {
    transition: transform 500ms linear, opacity ${EASE_OUT_DURATION}ms ease-out;
  }

  &:focus-visible {
    outline-style: solid;
    outline-width: 3px;
  }
`

const StyledText = styled(Text)`
  overflow: hidden;
  text-align: left;
  white-space: ${ifProp('limitLinesOfTextTo', 'normal', 'nowrap')};
`
const DividerBar = styled.div`
  margin-right: -16px;
  margin-left: -8px;
  width: 44px;
  border: 1px solid;
  border-color: ${getThemeColor('toolTipText', '#FFFFFF', 0.5)};
  transform: rotate(90deg) scale(0.5);
  &:focus {
    outline-style: none;
  }
`

const StyledCloseButton = styled.button`
  right: 0;
  cursor: pointer;
  border: none;
  background: none;
  height: 100%;
  margin: 0;
  border-radius: 8px;
  &:hover {
    & svg {
      fill-opacity: 0.8;
    }
  }
  &:active {
    & svg {
      fill-opacity: 0.6;
    }
  }

  &:focus-visible {
    outline-style: solid;
    outline-width: 3px;
    outline-offset: -4px;
  }
`

const Toast = forwardRef((props, ref) => {
  const [isVisible, setIsVisible] = useState(false)
  const toastRef = useRef()
  const textRef = useRef()
  const [isTextSmall, setIsTextSmall] = useState(false)
  const [mousePos, setMousePos] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  let dragDirection = null
  const directions = { horizontal: 'horizontal', vertical: 'vertical' }
  const {
    hideDelay,
    iconName,
    onToastDismiss,
    shouldDisplay,
    children,
    dismissButton,
    isDesktop,
    focusAfterToastDismissRef,
    T,
    ...omittedProps
  } = props

  const dismissButtonRef = useRef(null)
  const dividerRef = useRef(null)

  useEffect(() => {
    let id
    if (shouldDisplay) {
      setIsVisible(true)
      if (toastRef && toastRef.current) toastRef.current.classList.remove('hide') // remove fade out animation
    } else id = hideToast()
    return () => id && clearTimeout(id)
  }, [shouldDisplay]) // when parent requests visibility change

  useEffect(() => {
    let id
    const activeOutside = () => {
      if (toastRef.current && !toastRef.current.contains(document.activeElement)) hideToast()
    }
    if (isVisible) {
      document.addEventListener('click', activeOutside)
      document.addEventListener('keydown', activeOutside)
      if (dividerRef && dividerRef.current) dividerRef.current.focus() // once the toast has animated in, focus in the toast. Focusing here is kind of a hack, but allows the 'next tab' to end up on the close button.
      if (textRef && textRef.current)
        setIsTextSmall(
          textRef.current.offsetWidth < textRef.current.scrollWidth
        ) // set text to small if it overflows container

      if (!hideDelay) return // if the component shouldn't fade out after some delay, don't add animation
      id = setTimeout(() => {
        hideToast()
      }, hideDelay) // add delayed fade out when component becomes visible
    }
    return () => {
      document.removeEventListener('click', activeOutside)
      document.removeEventListener('keydown', activeOutside)
      id && clearTimeout(id)
    }
  }, [isVisible]) // when component visibility changes

  const hideToast = () => {
    if (toastRef && toastRef.current) toastRef.current.classList.add('hide') // add fade-out animation
    return setTimeout(() => {
      if (typeof onToastDismiss === 'function') onToastDismiss()
      setIsVisible(false)
      if (focusAfterToastDismissRef && focusAfterToastDismissRef.current) focusAfterToastDismissRef.current.focus() // Pass focus back to whatever bus call initiated the toast, if a ref was provided.
    }, EASE_OUT_DURATION) // fire toast dismiss action and remove toast from DOM after animation has ended
  }

  const closeOnClick = () => {
    hideToast()
  }

  const handleMouseDown = (e) => {
    e.preventDefault()

    if (e.type === 'touchstart') {
      setMousePos({ x: e.touches[0].clientX, y: e.touches[0].clientY })
    } else {
      setMousePos({ x: e.clientX, y: e.clientY })
    }
    setIsDragging(true)

    toastRef.current.classList.remove(
      'withHorizontalTransition',
      'withVerticalTransition'
    )
    dragDirection = null
  }
  const handleDrag = (e) => {
    e.preventDefault()
    if (!isDragging) return // if the drag wasn't started, don't move the toast

    let currentX, currentY
    if (e.type === 'touchmove') {
      currentX = e.touches[0].clientX
      currentY = e.touches[0].clientY
    } else {
      currentX = e.clientX
      currentY = e.clientY
    }

    // if drag direction wasn't detected yet
    if (dragDirection === null) {
      // handle both left and right
      if (Math.abs(currentX - mousePos.x) >= 10)
        dragDirection = directions.horizontal
      else if (mousePos.y - currentY >= 10) dragDirection = directions.vertical // handle only move to top
    }

    if (dragDirection === directions.horizontal) {
      // if drag was horizontal
      const moveBy =
      currentX > mousePos.x ? window.innerWidth : -window.innerWidth // move to the right or left
      animateToastSwipe({
        direction: directions.horizontal,
        moveBy
      })
    } else if (dragDirection === directions.vertical) {
      // if drag was vertical
      animateToastSwipe({
        direction: directions.vertical,
        moveBy: '-100%' // move only to the top
      })
    }
  }

  const animateToastSwipe = ({ direction, moveBy }) => {
    if (direction === directions.horizontal) {
      // if drag was horizontal
      toastRef.current.classList.add('withHorizontalTransition')
      toastRef.current.style.transform = `translateX(${moveBy}px)`
      setTimeout(() => {
        hideToast()
      }, 300) // allow drag-off animation to play for 300ms before fade-out will be added
    } else if (direction === directions.vertical) {
      // if drag was vertical
      toastRef.current.classList.add('withVerticalTransition')
      toastRef.current.style.transform = `translateY(${moveBy})`
      setTimeout(() => {
        hideToast()
      }, 100) // allow drag-off animation to play for 100ms before fade-out will be added
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const keyDownHandler = (e) => {
    if (e.code === 'Tab') {
      if (dismissButtonRef && dismissButtonRef.current) {
        if (document.activeElement === dismissButtonRef.current) {
          e.preventDefault()
        }
      }
    }
  }
  if (!isVisible) return null
  return (
    <StyledToast
      ref={toastRef}
      aria-live="assertive"
      role="alertdialog"
      aria-modal="true"
      aria-label={T('ui:Alert dialog')}
      onMouseDown={handleMouseDown}
      onTouchStart={handleMouseDown}
      onMouseMove={handleDrag}
      onTouchMove={handleDrag}
      onMouseUp={handleMouseUp}
      onMouseOut={handleMouseUp}
      onTouchEnd={handleMouseUp}
      onKeyDown={(e) => keyDownHandler(e)}
      display="flex"
      position='absolute'
      mt={isDesktop && '20px'}
      height="44px"
      width='auto'
      p="0px 6px 0px 12px"
      color="#ffffff"
      bg="#000"
      textAlign="center"
      alignItems="center"
      justifyContent="center"
      borderRadius='6px'
      isDesktop={isDesktop}
      {...omittedProps}
    >
      <Icon
        name={iconName}
        iconSize="md"
        color={props.color || '#FFFFFF'}
        as="span"
        height="28px"
        width="28px"
        display="inline-block"
        marginRight="4px"
        position="relative"
        verticalAlign="middle"
      />
      <StyledText
        data-cy="ToastText"
        ref={textRef}
        fontSize={isTextSmall ? 'sm' : 'md'}
        fontWeight="bold"
        limitLinesOfTextTo={isTextSmall ? 1 : 0}
      >
        {children}
      </StyledText>
      <DividerBar tabIndex={1} ref={dividerRef} data-cy='ToastDivider'/>
      {dismissButton && (
        <StyledCloseButton
          isDesktop={isDesktop}
          ref={dismissButtonRef}
          onClick={closeOnClick}
          color={props.color}
          data-cy="ToastDismissButton"
          type="button"
          aria-label={T('ui:Dismiss alert dialog')}
        >
          <Icon
            name={'close'}
            iconSize="md"
            color={props.color || '#FFFFFF'}
            as="span"
            height="28px"
            width="28px"
            display="inline-block"
            verticalAlign="middle"
            style={{ pointerEvents: 'none' }}
          />
        </StyledCloseButton>
      )}
    </StyledToast>
  )
})

Toast.displayName = 'Toast'

Toast.defaultProps = {
  'data-cy': 'Toast', // component name
  hideDelay: false,
  iconName: 'info',
  children: 'This is a generic alert message',
  shouldDisplay: false,
  dismissButton: true
}

Toast.propTypes = {
  ...styledSystemPropTypes.space,
  ...styledSystemPropTypes.color,
  ...styledSystemPropTypes.background,
  ...styledSystemPropTypes.border,
  ...styledSystemPropTypes.layout,
  ...styledSystemPropTypes.position,
  ...styledSystemPropTypes.shadow,
  as: PropTypes.string,
  forwardAs: PropTypes.string,
  hideDelay: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  iconName: PropTypes.string,
  onToastDismiss: PropTypes.func,
  shouldDisplay: PropTypes.bool,
  dismissButton: PropTypes.bool,
  focusAfterToastDismissRef: PropTypes.func
}

export default Toast
